import { useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../Dialog/Dialog"
import { PaymentMethodCard } from "../PaymentMethodCard/PaymentMethodCard"
import type { PaymentMethod } from "../types/payment"

export interface PaymentMethodListProps {
  methods: PaymentMethod[]
  addLabel?: string
  removeTitle?: string
  removeDescription?: (method: PaymentMethod) => ReactNode
  removeConfirmLabel?: string
  removeCancelLabel?: string
  onSetDefault?: (method: PaymentMethod) => void
  onDelete?: (method: PaymentMethod) => void
  onAdd?: () => void
  className?: string
}

export function PaymentMethodList({
  methods,
  addLabel = "Add Payment Method",
  removeTitle = "Remove payment method?",
  removeDescription,
  removeConfirmLabel = "Remove",
  removeCancelLabel = "Keep",
  onSetDefault,
  onDelete,
  onAdd,
  className,
}: PaymentMethodListProps) {
  const [pendingDelete, setPendingDelete] = useState<PaymentMethod | null>(null)

  const resolveDescription = (method: PaymentMethod): ReactNode => {
    if (removeDescription) return removeDescription(method)
    return (
      <>
        {method.brand} •••• {method.last4} will be removed. This cannot be undone.
      </>
    )
  }

  const handleConfirm = () => {
    if (pendingDelete) {
      onDelete?.(pendingDelete)
      setPendingDelete(null)
    }
  }

  return (
    <div className={cn("w-full", className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {methods.map((method) => (
          <PaymentMethodCard
            key={method.id}
            method={method}
            onSetDefault={onSetDefault}
            onDelete={onDelete ? (m) => setPendingDelete(m) : undefined}
          />
        ))}
      </div>

      {onAdd && (
        <Button
          size="sm"
          variant="outline"
          className="mt-3 text-label"
          onClick={onAdd}
        >
          {addLabel}
        </Button>
      )}

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <DialogContent className="v1 sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-section text-foreground">{removeTitle}</DialogTitle>
            <DialogDescription className="text-body text-muted-foreground">
              {pendingDelete && resolveDescription(pendingDelete)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-label"
              onClick={() => setPendingDelete(null)}
            >
              {removeCancelLabel}
            </Button>
            <Button size="sm" className="text-label" onClick={handleConfirm}>
              {removeConfirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
